import { useEffect, useState } from 'react'
import { Variants, motion } from 'framer-motion'
import moment from 'moment'
import DOMPurify from 'dompurify'
import { useTranslation } from 'react-i18next'
import type {
  AiinsightspbPeriod,
  AiinsightspbAIInsightsResponse
} from '@itsrever/rever-api'
import { ReturnRateAIInsightsFeedback } from './return-rate-ai-insights-feedback'
import { usePostHog } from 'posthog-js/react'
import { useEcommerceId } from '@/hooks'

function formatPeriodDisplay(period: AiinsightspbPeriod) {
  const fromDate = moment(period.from_date, 'YYYY-MM')
  const toDate = moment(period.to_date, 'YYYY-MM')

  if (fromDate.year() === toDate.year()) {
    const fromMonth = fromDate.format('MMM')
    const toMonth = toDate.format('MMM, YYYY')
    return `${fromMonth} - ${toMonth}`
  } else {
    const fromFormatted = fromDate.format('MMM, YYYY')
    const toFormatted = toDate.format('MMM, YYYY')
    return `${fromFormatted} - ${toFormatted}`
  }
}

export const ReturnRateAIInsightsContent = ({
  data
}: {
  data: AiinsightspbAIInsightsResponse
}) => {
  const { t } = useTranslation()

  const posthog = usePostHog()

  const ecommerceId = useEcommerceId()

  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState<number[]>([])

  useEffect(() => {
    const createLoading = setTimeout(() => {
      setLoading(false)
    }, 5000)

    return () => {
      clearTimeout(createLoading)
    }
  }, [])

  const onExpandInsight = (isExpanded: boolean, id: number) => {
    if (isExpanded) {
      const removed = expanded.filter((value) => value !== id)

      setExpanded(removed)

      return
    }

    posthog?.capture('Clicked to expand return rate insight', {
      ecommerceId,
      insightId: id
    })

    setExpanded((prev) => [...prev, id])

    return
  }

  return (
    <div className="h-full overflow-y-scroll p-5">
      {loading ? (
        <div className="flex items-center gap-2.5 text-sm">
          <span className="text-neutral-400">
            <ChartColumnIncreasingIcon />
          </span>
          <p className="before:scale-250 relative  overflow-hidden text-base font-medium text-neutral-700 before:absolute before:-left-1 before:top-0 before:z-10 before:block before:aspect-square before:w-full before:-translate-y-1/2 before:rotate-[-200deg] before:animate-shine before:bg-shine-gradient before:bg-[length:200%_auto]">
            {t('returns_analytics.generating_ai_insights')}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {data.content_insights?.map(({ insight, period, id }, index) => {
            const isExpanded = id ? expanded.includes(id) : false

            return (
              <div
                className="translate-y-5 opacity-0 blur-sm group-data-[open=true]:animate-fadeInUp"
                key={id}
                style={{
                  animationDelay: `${0.3 + index * 0.7}s`,
                  animationFillMode: 'forwards'
                }}
              >
                <div className="flex items-center justify-between gap-4 pb-2">
                  <h3 className="text-sm font-medium">{insight?.title}</h3>{' '}
                  <p className="flex items-center gap-2 whitespace-nowrap !text-xs font-normal text-neutral-500">
                    <svg
                      width="12px"
                      height="12px"
                      strokeWidth="1.5"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      color="currentColor"
                    >
                      <path
                        d="M12 6L12 12L18 12"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>{' '}
                    {period ? formatPeriodDisplay(period) : '-'}
                  </p>
                </div>
                {insight?.description ? (
                  <div
                    data-expanded={isExpanded}
                    className="relative line-clamp-3 font-[Roobert] text-xs font-normal leading-relaxed text-neutral-600 backdrop-blur-sm after:absolute after:bottom-0 after:left-0 after:z-10 after:h-5 after:w-full after:bg-gradient-to-t after:from-white/90 after:to-white/30 data-[expanded=true]:line-clamp-none data-[expanded=true]:after:hidden [&_*]:!text-xs [&_*]:!leading-relaxed [&_*]:!text-neutral-600 [&_p]:font-normal [&_ul]:list-disc [&_ul]:ps-2.5"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(insight?.description)
                    }}
                  />
                ) : null}

                <div className="flex items-center justify-between pt-1.5">
                  <button
                    className="cursor-pointer text-xs text-primary-main hover:underline hover:underline-offset-4"
                    onClick={() => id && onExpandInsight(isExpanded, id)}
                  >
                    {isExpanded
                      ? t('returns_analytics.see_less')
                      : t('returns_analytics.see_more')}
                  </button>
                  <ReturnRateAIInsightsFeedback insightId={id} />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const lineVariants: Variants = {
  visible: {
    pathLength: 1,
    opacity: 1
  },
  looping: (i: number) => ({
    pathLength: [1, 0, 1],
    opacity: [1, 0, 1],
    transition: {
      duration: 1.1,
      times: [0, 0.5, 1],
      delay: i * 0.1,
      repeat: Infinity,
      repeatDelay: 0.5,
      repeatType: 'loop' as const
    }
  })
}

const ChartColumnIncreasingIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={20}
      height={20}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <motion.path
        variants={lineVariants}
        initial="visible"
        animate="looping"
        custom={0}
        d="M8 17v-3"
      />
      <motion.path
        variants={lineVariants}
        initial="visible"
        animate="looping"
        custom={1}
        d="M13 17V9"
      />
      <motion.path
        variants={lineVariants}
        initial="visible"
        animate="looping"
        custom={2}
        d="M18 17V5"
      />
      <path d="M3 3v16a2 2 0 0 0 2 2h16" />
    </svg>
  )
}
